import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { Button, Label, Stack, useToast } from '@eluve/components';
import { FragmentOf } from '@eluve/graphql.tada';
import { SupportedLanguages } from '@eluve/language-utils';
import { MEDICAL_SPECIALTIES } from '@eluve/utils';

import {
  insertTemplateBundleDefaultMutation,
  updateTemplateBundleDefaultMutation,
} from './bundle-defaults.operations';
import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from './template-bundle.operations';

export type TemplateBundleOption = FragmentOf<
  typeof templateBundleListItemFragment
>;

export type Scope = { kind: 'tenant'; tenantId: string };

export interface BundleDefaultRow {
  id: string;
  tenantId: string | null;
  templateBundleId: string;
  specialty: string | null;
  language: string | null;
}

interface BundleDefaultRowFormProps {
  scope: Scope;
  initialValue?: BundleDefaultRow;
  bundles: TemplateBundleOption[];
  onSaved: () => void;
  onCancel: () => void;
}

const isDuplicateRowError = (message: string): boolean =>
  /uniq_template_bundle_default_lookup/i.test(message) ||
  /23505/.test(message) ||
  /unique constraint/i.test(message);

const NULL_OPTION_VALUE = '__null__';

export const BundleDefaultRowForm: React.FC<BundleDefaultRowFormProps> = ({
  scope,
  initialValue,
  bundles,
  onSaved,
  onCancel,
}) => {
  const toast = useToast();

  const [templateBundleId, setTemplateBundleId] = useState<string>(
    initialValue?.templateBundleId ?? '',
  );
  const [specialty, setSpecialty] = useState<string | null>(
    initialValue?.specialty ?? null,
  );
  const [language, setLanguage] = useState<string | null>(
    initialValue?.language ?? null,
  );

  const { tenantId } = scope;

  const [insertDefault, { loading: inserting }] = useMutation(
    insertTemplateBundleDefaultMutation,
    { context: eluveAdminHasuraContext },
  );
  const [updateDefault, { loading: updating }] = useMutation(
    updateTemplateBundleDefaultMutation,
    { context: eluveAdminHasuraContext },
  );

  const saving = inserting || updating;
  const isUpdate = Boolean(initialValue?.id);

  // When editing a saved row whose bundle is no longer eligible (deactivated
  // globally or disabled for the tenant), the select would otherwise render
  // nothing matching the stored id — silently masking the broken default and
  // letting the admin save it again. Surface the orphan as a disabled option
  // so the admin sees the issue and must pick a valid replacement.
  const initialBundleId = initialValue?.templateBundleId;
  const initialBundleMissing = Boolean(
    initialBundleId && !bundles.some((b) => b.id === initialBundleId),
  );
  const hasInvalidSelection =
    Boolean(templateBundleId) &&
    !bundles.some((b) => b.id === templateBundleId);

  const handleError = (message: string) => {
    if (isDuplicateRowError(message)) {
      toast.error(
        'A default already exists for this scope + specialty + language. Edit that row instead.',
      );
    } else {
      toast.error(`Failed to save default: ${message}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateBundleId) return;
    if (hasInvalidSelection) {
      toast.error(
        'This default points to a bundle that is no longer available. Pick a different bundle to save.',
      );
      return;
    }

    const variables = {
      tenantId,
      templateBundleId,
      specialty,
      language,
    };

    try {
      if (isUpdate && initialValue) {
        const result = await updateDefault({
          variables: { id: initialValue.id, ...variables },
        });
        if (result.errors?.length) {
          handleError(result.errors.map((err) => err.message).join('; '));
          return;
        }
      } else {
        const result = await insertDefault({ variables });
        if (result.errors?.length) {
          handleError(result.errors.map((err) => err.message).join('; '));
          return;
        }
      }
      onSaved();
    } catch (err) {
      handleError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={4} align="stretch" className="text-sm">
        <Stack gap={1}>
          <Label htmlFor="bundle-default-bundle">Bundle</Label>
          <select
            id="bundle-default-bundle"
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
            value={templateBundleId}
            onChange={(e) => setTemplateBundleId(e.target.value)}
            required
          >
            <option value="" disabled>
              Select a bundle
            </option>
            {initialBundleMissing && initialBundleId && (
              <option value={initialBundleId} disabled>
                Current (no longer available)
              </option>
            )}
            {bundles.map((bundle) => (
              <option key={bundle.id} value={bundle.id}>
                {getTemplateBundleDisplayName(bundle)}
              </option>
            ))}
          </select>
          {hasInvalidSelection && (
            <p className="text-xs text-destructive">
              This bundle is no longer available. Pick a different bundle to
              save.
            </p>
          )}
        </Stack>

        <Stack gap={1}>
          <Label htmlFor="bundle-default-specialty">Specialty (optional)</Label>
          <select
            id="bundle-default-specialty"
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
            value={specialty ?? NULL_OPTION_VALUE}
            onChange={(e) =>
              setSpecialty(
                e.target.value === NULL_OPTION_VALUE ? null : e.target.value,
              )
            }
          >
            <option value={NULL_OPTION_VALUE}>Any (wildcard)</option>
            {MEDICAL_SPECIALTIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Stack>

        <Stack gap={1}>
          <Label htmlFor="bundle-default-language">Language (optional)</Label>
          <select
            id="bundle-default-language"
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
            value={language ?? NULL_OPTION_VALUE}
            onChange={(e) =>
              setLanguage(
                e.target.value === NULL_OPTION_VALUE ? null : e.target.value,
              )
            }
          >
            <option value={NULL_OPTION_VALUE}>Any (wildcard)</option>
            {Object.entries(SupportedLanguages).map(([name, code]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </Stack>

        <Stack gap={2} direction="horizontal" justify="end">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!templateBundleId || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
